import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useMutation } from "@apollo/client/react";
import { useNavigate } from "react-router-dom";
import { SIGNUP_USER } from "../graphql/mutation";
import CustomSnackbar from "../components/Snackbar";
import {
  SignupUserResponse,
  SignupUserVariables,
  SnackbarType,
} from "../interfaces/auth.interface";
import themestyle from "../theme";
import Copyright from "../components/Copyright";
import { REGEX, MESSAGES } from "../utils/const";
import { validateAdminField } from "../utils/validation";

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<SnackbarType>("error");

  const [signupUser, { loading }] = useMutation<
    SignupUserResponse,
    SignupUserVariables
  >(SIGNUP_USER);

  useEffect(() => {
    document.title = "Sign Up | United Grooves Admin Panel";
  }, []);

  const validateEmail = () => {
    if (!email) {
      setEmailError(MESSAGES.EMAIL_REQUIRED);
      return false;
    }
    if (!REGEX.EMAIL.test(email)) {
      setEmailError(MESSAGES.EMAIL_VALID);
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (value: string = password) => {
    const error = validateAdminField("password", value);
    setPasswordError(error);
    return !error;
  };

  const validateConfirmPassword = (value: string = confirmPassword) => {
    if (!value) {
      setConfirmPasswordError("Confirm password is required");
      return false;
    }
    if (value !== password) {
      setConfirmPasswordError(MESSAGES.PASSWORD_NOT_MATCH);
      return false;
    }
    const error = validateAdminField("password", value);
    setConfirmPasswordError(error);
    return !error;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPassword(val);
    validatePassword(val);
  };
  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setConfirmPassword(val);
    validateConfirmPassword(val);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const isEmailValid = validateEmail();
    const isPasswordValid = validatePassword();
    const isConfirmValid = validateConfirmPassword();

    if (!isEmailValid || !isPasswordValid || !isConfirmValid) return;

    try {
      const { data } = await signupUser({
        variables: {
          input: { email, password },
        },
      });

      if (!data?.signupUser?.success) {
        setSnackbarMessage(
          data?.signupUser?.message || "Signup failed",
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        return;
      }
      setSnackbarMessage(data.signupUser.message);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/"), 1200);
      
    } catch (error: any) {
      setSnackbarMessage(error.message || "An error occurred during signup");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const isFormValid =
    email.length > 0 &&
    password.length >= 8 &&
    confirmPassword === password &&
    emailError === "" &&
    passwordError === "" &&
    confirmPasswordError === "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 4, sm: 3, md: 6 },
          bgcolor: themestyle.colors.white,
          position: "relative",
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              gap: 1.2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src="united-grooves-logo.png"
              alt="United Grooves"
              sx={{ width: 22, height: 31 }}
            />
            <Typography
              sx={{
                fontFamily: themestyle.fontFamily.Roboto,
                fontWeight: 500,
                fontSize: "16px",
                letterSpacing: "1px",
                mt: "3px",
                ml: "4px",
              }}
            >
              {MESSAGES.BRAND_NAME}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: themestyle.fontFamily.Roboto,
              fontWeight: 500,
              fontSize: { xs: "30px", sm: "2.8rem", md: "2.8rem" },
              mb: 1,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.SIGNUP_TITLE}
          </Typography>
          <Typography
            sx={{
              color: themestyle.colors.textSecondary,
              fontFamily: themestyle.fontFamily.Roboto,
              fontSize: { xs: "14px", sm: "16px", md: "16px" },
              mb: 4,
              ml: { xs: 0, sm: -1, md: -1 },
            }}
          >
            {MESSAGES.SIGNUP_SUBTITLE}
          </Typography>
          <form onSubmit={handleSignup}>
            <Box sx={{ mb: 1.5 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.EMAIL_LABEL}
              </Typography>
              <TextField
                fullWidth
                type="email"
                placeholder={MESSAGES.EMAIL_PLACEHOLDER}
                value={email}
                onChange={handleEmailChange}
                autoComplete="off"
                onBlur={validateEmail}
                error={!!emailError}
                helperText={emailError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "9px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ mb: 1.5, mt: 3 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.PASSWORD_LABEL}
              </Typography>
              <TextField
                fullWidth
                placeholder={MESSAGES.PASSWORD_PLACEHOLDER}
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={handlePasswordChange}
                autoComplete="new-password"
                onBlur={() => validatePassword()}
                error={!!passwordError}
                helperText={passwordError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "8px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        size="small"
                        sx={{
                          "& svg": { fontSize: { xs: 18, sm: 20, md: 24 } },
                        }}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box sx={{ mb: 3, mt: 3 }}>
              <Typography
                sx={{
                  mb: 0.8,
                  fontSize: { xs: "10px", sm: "0.9rem" },
                  fontWeight: 600,
                }}
              >
                {MESSAGES.CONFIRM_PASSWORD_LABEL}
              </Typography>
              <TextField
                fullWidth
                placeholder={MESSAGES.CONFIRM_PASSWORD_PLACEHOLDER}
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                autoComplete="new-password"
                onBlur={() => validateConfirmPassword()}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
                FormHelperTextProps={{
                  sx: {
                    fontSize: { xs: "8px", sm: "0.75rem" },
                    marginTop: "4px",
                    marginLeft: "2px",
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: { xs: "40px", sm: "46px" },
                    "& input": {
                      fontSize: { xs: "10px", sm: "14px" },
                      padding: { xs: "8px 12px", sm: "10px 12px" },
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: themestyle.colors.activeGreen,
                      borderWidth: "1px",
                    },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        size="small"
                        sx={{
                          "& svg": { fontSize: { xs: 18, sm: 20, md: 24 } },
                        }}
                      >
                        {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Button
              type="submit"
              fullWidth
              disabled={!isFormValid || loading}
              sx={{
                width: "100%",
                mt: { xs: 0.4, md: 0.8 },
                backgroundColor: themestyle.colors.activeGreen,
                color: themestyle.colors.white,
                fontSize: { xs: "11px", sm: "16px" },
                py: 1.2,
                height: { xs: "38px", sm: "auto" },
                textTransform: "none",
                borderRadius: "5px",
                "&.Mui-disabled": {
                  backgroundColor: "#9E9E9E",
                  color: themestyle.colors.white,
                },
                "&:hover": {
                  backgroundColor: themestyle.colors.secondary,
                },
              }}
            >
              {loading ? "Signing up..." : MESSAGES.SIGN_UP}
            </Button>
            <Box
              sx={{
                mt: { xs: 1.2, sm: 1.8, md: 2.3 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  fontFamily: themestyle.fontFamily.Roboto,
                  fontSize: { xs: "10px", sm: "16px", md: "14px" },
                  color: themestyle.colors.textSecondary,
                  fontWeight: 400,
                }}
              >
                {MESSAGES.ALREADY_HAVE_ACCOUNT}{" "}
                <Typography
                  component="span"
                  sx={{
                    fontSize: { xs: "10px", sm: "16px", md: "14px" },
                    fontFamily: themestyle.fontFamily.Roboto,
                    color: themestyle.colors.activeGreen,
                    fontWeight: 400,
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => navigate("/")}
                >
                  {MESSAGES.SIGN_IN}
                </Typography>
              </Typography>
            </Box>
          </form>
          <Box sx={{ display: { xs: "block", md: "none" }, mt: 4 }}>
            <Copyright />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          bgcolor: themestyle.colors.black,
          position: "relative",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Copyright />
        <Box textAlign="center">
          <Box
            component="img"
            src="united-grooves-logo.png"
            alt="United Grooves"
            sx={{ width: { xs: 80, md: 200 }, mb: 2 }}
          />
          <Typography
            sx={{
              color: themestyle.colors.white,
              fontSize: { xs: 18, md: 40 },
              letterSpacing: { xs: "2px", md: "12px" },
            }}
          >
            {MESSAGES.BRAND_NAME}
          </Typography>
        </Box>
      </Box>
      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarSeverity}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};
export default Signup;
